/**
 * StudioSTXDigitalPage — iter127
 * Creator-Ansicht aller STX-Digital-Entitlements ihrer Käufer.
 * Ermöglicht Release-Status-Übergänge:
 *   NOT_AVAILABLE → AWAITING_UPLOAD → UPLOADED → MODERATION_PENDING → READY_FOR_RELEASE → AVAILABLE
 * Sobald AVAILABLE: Käufer bekommt automatische Notification (Backend).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, RefreshCcw, Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

const NEXT_STATE = {
  NOT_AVAILABLE: "AWAITING_UPLOAD",
  AWAITING_UPLOAD: "UPLOADED",
  UPLOADED: "MODERATION_PENDING",
  MODERATION_PENDING: "READY_FOR_RELEASE",
  READY_FOR_RELEASE: "AVAILABLE",
};

const STATE_COLORS = {
  NOT_AVAILABLE: "bg-gray-100 text-gray-600 border-gray-300",
  AWAITING_UPLOAD: "bg-blue-50 text-blue-700 border-blue-200",
  UPLOADED: "bg-cyan-50 text-cyan-700 border-cyan-200",
  MODERATION_PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  READY_FOR_RELEASE: "bg-purple-50 text-purple-700 border-purple-200",
  AVAILABLE: "bg-green-50 text-green-700 border-green-300",
  REVOKED: "bg-red-50 text-red-700 border-red-300",
  REMOVED: "bg-gray-100 text-gray-500 border-gray-300",
};

export default function StudioSTXDigitalPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/studio/stx/digital-entitlements");
      setEntitlements(data.entitlements || []);
    } catch (e) {
      setError(formatError(e) || "Konnte STX-Entitlements nicht laden");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const transition = async (eid, next) => {
    setBusyId(eid); setError("");
    try {
      await api.post(`/studio/entitlements/${eid}/release-status`, { new_status: next });
      await load();
    } catch (e) {
      setError(formatError(e) || "Übergang nicht möglich");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="studio-stx-digital-page" className="w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-4">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
        <button onClick={() => navigate("/studio")} data-testid="stx-digital-back" className="p-1 rounded-full hover:bg-[#F5F5F5]">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-lg font-bold text-[#0A0A0A] flex-1">STX-Digital-Inhalte</h1>
        <button onClick={load} data-testid="stx-digital-refresh" className="p-2 rounded-full hover:bg-[#F5F5F5]">
          <RefreshCcw size={16} />
        </button>
      </div>

      <p className="text-xs text-[#666] px-4 py-3">
        Sobald du einen Inhalt auf <strong>AVAILABLE</strong> setzt, erhält der Käufer automatisch eine Benachrichtigung
        und kann den Inhalt herunterladen. Vorherige Status dienen dem Freigabe-Workflow.
      </p>

      {error && (
        <InfoCard variant="danger" size="sm" className="mx-4 mb-3 rounded" icon={<AlertTriangle size={14} />}>
          {error}
        </InfoCard>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : entitlements.length === 0 ? (
        <p data-testid="stx-digital-empty" className="text-center text-sm text-[#999] py-16">
          Noch keine STX-Digital-Entitlements ausgeliefert. Diese Ansicht befüllt sich, sobald ein Käufer digitale Inhalte deiner STX-Bestellungen erwirbt.
        </p>
      ) : (
        <ul className="flex flex-col gap-2 px-4">
          {entitlements.map((e) => {
            const state = e.release_status || "NOT_AVAILABLE";
            const nxt = NEXT_STATE[state];
            const canRevoke = state !== "REVOKED" && state !== "REMOVED";
            return (
              <li key={e.entitlement_id} data-testid={`stx-ent-${e.entitlement_id}`} className="border border-[#E5E5E5] rounded-2xl p-3 bg-white">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate">{e.resource_id}</p>
                    <p className="text-xs text-[#666] mt-0.5">
                      Käufer: <strong>{e.buyer?.display_name || e.buyer?.handle || e.subject_id?.slice(-8)}</strong>
                    </p>
                    {e.stx_order_id && <p className="text-[10px] text-[#999]">Bestellung: {e.stx_order_id.slice(-8)}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATE_COLORS[state] || STATE_COLORS.NOT_AVAILABLE}`}>
                        {state}
                      </span>
                      <span className="text-[10px] text-[#999]">
                        {e.acquisition_type?.replaceAll("_", " ")}
                      </span>
                      {e.download_count > 0 && (
                        <span className="text-[10px] text-[#666]">DL: {e.download_count}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {nxt && (
                      <button
                        data-testid={`stx-ent-next-${e.entitlement_id}`}
                        onClick={() => transition(e.entitlement_id, nxt)}
                        disabled={busyId === e.entitlement_id}
                        className="flex items-center gap-1 h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-xs disabled:opacity-40 hover:bg-[#222]"
                      >
                        {busyId === e.entitlement_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        → {nxt.replaceAll("_", " ")}
                      </button>
                    )}
                    {canRevoke && (
                      <button
                        data-testid={`stx-ent-revoke-${e.entitlement_id}`}
                        onClick={() => {
                          if (window.confirm(t("studio.revoke_confirm"))) {
                            transition(e.entitlement_id, "REVOKED");
                          }
                        }}
                        disabled={busyId === e.entitlement_id}
                        className="flex items-center gap-1 h-8 px-3 rounded-full border border-red-300 text-red-700 text-xs disabled:opacity-40 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Revoke
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
